import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Badge, Button } from "@/components/ui/PageHeader";
import {
  getBalance,
  getHistory,
  getPartners,
  getMyRedemptions,
  getPartnerOffers,
  redeemOffer,
  type YashPartner,
} from "@/api/yash";

export default function YashLab() {
  const { data: balance } = useQuery({
    queryKey: ["yash-balance"],
    queryFn: getBalance,
  });
  const { data: history } = useQuery({
    queryKey: ["yash-history"],
    queryFn: () => getHistory({ limit: 20 }),
  });
  const { data: partners } = useQuery({
    queryKey: ["yash-partners"],
    queryFn: getPartners,
  });
  const { data: redemptions } = useQuery({
    queryKey: ["yash-redemptions"],
    queryFn: getMyRedemptions,
  });

  return (
    <div className="space-y-8">
      <PageHeader
        title="Yash (Rewards)"
        subtitle="Your balance, transaction history, partners, redemptions."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Yash" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Balance</h2>
        {balance ? (
          <div className="grid grid-cols-3 gap-6">
            <Stat label="Available" value={balance.data.balance} accent />
            <Stat label="Earned" value={balance.data.totalEarned} />
            <Stat label="Spent" value={balance.data.totalSpent} />
          </div>
        ) : (
          <p className="text-sm text-gray-500">Loading…</p>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Recent activity
        </h2>
        {!history || history.data.transactions.length === 0 ? (
          <p className="text-sm text-gray-500">No transactions yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {history.data.transactions.map((entry) => (
              <li key={entry.id} className="py-2 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900 text-sm">
                    {entry.actionType ?? entry.type}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {new Date(entry.createdAt).toLocaleString()}
                  </div>
                </div>
                <Badge variant={entry.yash > 0 ? "green" : "red"}>
                  {entry.yash > 0 ? "+" : ""}
                  {entry.yash}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Partners</h2>
          {!partners || partners.data.partners.length === 0 ? (
            <p className="text-sm text-gray-500">No partners yet.</p>
          ) : (
            <ul className="space-y-2">
              {partners.data.partners.map((p) => (
                <PartnerCard key={p.id} p={p} />
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            My redemptions
          </h2>
          {!redemptions || redemptions.data.redemptions.length === 0 ? (
            <p className="text-sm text-gray-500">No redemptions yet.</p>
          ) : (
            <ul className="space-y-2">
              {redemptions.data.redemptions.map((r) => (
                <li
                  key={r.id}
                  className="border border-gray-200 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 text-sm">
                      {r.offer.title}
                    </span>
                    <Badge
                      variant={
                        r.status === "active"
                          ? "green"
                          : r.status === "used"
                            ? "gray"
                            : "yellow"
                      }
                    >
                      {r.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.yashSpent} yash · {new Date(r.issuedAt).toLocaleDateString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function PartnerCard({ p }: { p: YashPartner }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const { data: detail, isLoading } = useQuery({
    queryKey: ["yash-partner-offers", p.id],
    queryFn: () => getPartnerOffers(p.id),
    enabled: open,
  });

  async function redeem(offerId: string) {
    setRedeemingId(offerId);
    setResult(null);
    try {
      const res = await redeemOffer(offerId);
      setResult(
        `Redeemed! Code ${res.data.redemptionCode} (expires ${new Date(res.data.expiresAt).toLocaleString()})`,
      );
      qc.invalidateQueries({ queryKey: ["yash-balance"] });
      qc.invalidateQueries({ queryKey: ["yash-redemptions"] });
      qc.invalidateQueries({ queryKey: ["yash-partner-offers", p.id] });
    } catch (err) {
      setResult(err instanceof Error ? err.message : "Redemption failed");
    } finally {
      setRedeemingId(null);
    }
  }

  return (
    <li className="border border-gray-200 rounded-lg px-3 py-2">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left flex items-center justify-between"
      >
        <div>
          <div className="font-medium text-gray-900 text-sm">{p.businessName}</div>
          {p.category && (
            <div className="text-xs text-gray-500">{p.category}</div>
          )}
        </div>
        <span className="text-xs text-gray-400">{open ? "Hide" : "Offers"}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {result && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 text-xs px-2 py-1.5 rounded">
              {result}
            </div>
          )}
          {isLoading ? (
            <p className="text-xs text-gray-500">Loading offers…</p>
          ) : !detail || detail.data.partner.offers.length === 0 ? (
            <p className="text-xs text-gray-500">No offers.</p>
          ) : (
            detail.data.partner.offers.map((o) => (
              <div
                key={o.id}
                className="flex items-start justify-between gap-2 border-t border-gray-100 pt-2"
              >
                <div className="min-w-0">
                  <div className="text-sm text-gray-900">{o.title}</div>
                  <div className="text-xs text-gray-500">
                    {o.yashRequired} Yash
                    {!o.isAvailable && " · sold out"}
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={!o.isAvailable || redeemingId === o.id}
                  isLoading={redeemingId === o.id}
                  onClick={() => redeem(o.id)}
                >
                  Redeem
                </Button>
              </div>
            ))
          )}
        </div>
      )}
    </li>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div
        className={`text-3xl font-bold ${accent ? "gradient-text" : "text-gray-900"}`}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}
